import type { CogoKnowledgeRepository, StoreScopedValidation } from '@cogo/kg-repository'
import type { BlockDef } from '@cogo/types-v2'
import type { ShaclViolation } from '@cogo/kg-browser'
import { extractBlockJsonLd } from '@/features/validation/extract-block-jsonld'
import type {
  AppValidationReport,
  PerBlockResult,
  PhaseResult,
} from './types'
import { aggregateReport } from './app-validation-aggregator'

function syntheticViolation(shape: string, message: string): ShaclViolation {
  return { shape, severity: 'violation', message, bindings: {} }
}

function emptyPhase(): PhaseResult {
  return { violations: [], durationMs: 0, rulesEvaluated: 0 }
}

export async function runValidationForApp(
  repo: CogoKnowledgeRepository & StoreScopedValidation,
  blocks: Record<string, BlockDef>,
): Promise<AppValidationReport> {
  const t0 = performance.now()
  const perBlock: PerBlockResult[] = []

  for (const [blockId, block] of Object.entries(blocks)) {
    let payload
    try {
      payload = extractBlockJsonLd(
        block as unknown as { ui?: unknown; flow?: unknown; events?: unknown },
      )
    } catch (err) {
      perBlock.push({
        blockId,
        blockName: blockId,
        phases: {
          structural: {
            ...emptyPhase(),
            violations: [
              syntheticViolation(
                `extract-block-jsonld:${blockId}`,
                `Failed to extract block JSON-LD: ${err instanceof Error ? err.message : String(err)}`,
              ),
            ],
          },
          binding: emptyPhase(),
        },
      })
      continue
    }

    let graphIri: string | null = null
    try {
      graphIri = await repo.loadJsonLd(payload)
      const [structural, binding] = await Promise.all([
        repo.validate('structural'),
        repo.validate('binding'),
      ])
      perBlock.push({
        blockId,
        blockName: blockId,
        phases: {
          structural: {
            violations: structural.data.violations as ShaclViolation[],
            durationMs: structural.meta.durationMs,
            rulesEvaluated: structural.data.totalRules,
          },
          binding: {
            violations: binding.data.violations as ShaclViolation[],
            durationMs: binding.meta.durationMs,
            rulesEvaluated: binding.data.totalRules,
          },
        },
      })
    } catch (err) {
      perBlock.push({
        blockId,
        blockName: blockId,
        phases: {
          structural: {
            ...emptyPhase(),
            violations: [
              syntheticViolation(
                `validate-error:${blockId}`,
                `Validate threw: ${err instanceof Error ? err.message : String(err)}`,
              ),
            ],
          },
          binding: emptyPhase(),
        },
      })
    } finally {
      if (graphIri) {
        try {
          await repo.dropGraph(graphIri)
        } catch {
          // best-effort cleanup
        }
      }
    }
  }

  return aggregateReport(perBlock, performance.now() - t0)
}
